import { useState, useEffect, useCallback } from "react"
import { useAuthState } from "react-firebase-hooks/auth"
import { auth } from "../firebase"
import api from "../api_ui"

const useUser = () => {
    const [user] = useAuthState(auth)
    const [currentUser, setCurrentUser] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [retryCount, setRetryCount] = useState(0)
    const [refreshTrigger, setRefreshTrigger] = useState(0)

    const fetchUserData = useCallback(async () => {
        if (user) {
            console.log("useUser - Fetching data for user:", user.uid)
            setLoading(true)
            try {
                const { data } = await api.get_user(user.uid)

                // If user not found and we haven't retried too many times, retry after a delay
                if (!data && retryCount < 3) {
                    console.log(
                        `useUser - No data found, retrying... (attempt ${
                            retryCount + 1
                        }/3)`
                    )
                    setTimeout(() => {
                        setRetryCount((prev) => prev + 1)
                    }, 1000 * (retryCount + 1)) // Exponential backoff: 1s, 2s, 3s
                    return
                }

                // If still no data after retries, set loading to false
                if (!data) {
                    console.log(
                        "useUser - No data found for user after retries"
                    )
                    setLoading(false)
                    return
                }

                const {
                    id,
                    name,
                    email,
                    daysLeftForPremium,
                    isPremiumExpired,
                    isPremium,
                    isAgreementAccept = false // Default to false if not provided
                } = data

                // Check if user is on trial
                const isTrialUser = !isPremium || isPremiumExpired

                const userData = {
                    id,
                    email,
                    displayName: name,
                    photoURL: user.photoURL,
                    daysLeftForPremium,
                    isPremiumExpired,
                    isPremium,
                    isTrialUser,
                    isAgreementAccept,
                    isVerified: user.emailVerified
                }
                console.log("useUser - Setting currentUser:", userData)
                setCurrentUser(userData)
                setRetryCount(0) // Reset retry count on success
            } catch (err) {
                setError(err)
            } finally {
                setLoading(false)
            }
        } else {
            setLoading(false)
            setRetryCount(0) // Reset retry count when no user
        }
    }, [user, retryCount])

    // Manual refresh function
    const refreshUserData = useCallback(() => {
        console.log("useUser - Manual refresh triggered")
        setRefreshTrigger((prev) => prev + 1)
    }, [])

    useEffect(() => {
        fetchUserData()
    }, [fetchUserData, refreshTrigger])

    return { currentUser, loading, error, refreshUserData }
}

export default useUser
